import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Hash, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/utils/api";

const SharePage = () => {
  const { certificateId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCertificate();
  }, [certificateId]);

  const fetchCertificate = async () => {
    try {
      // Use the share endpoint which returns masked ID and view-only data
      const response = await axios.get(`${API}/certificates/${certificateId}/share`);
      setCertificate(response.data);
    } catch (err) {
      console.error("Error fetching certificate:", err);
      if (err.response?.status === 402) {
        setError("This certificate is not available for viewing.");
      } else {
        setError(err.response?.data?.detail || "Certificate not found.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF7]" data-testid="share-loading">
        <div className="text-center">
          <Heart className="w-12 h-12 text-[#E8A0BF] animate-pulse mx-auto mb-4" />
          <p className="text-[#5A5A5A]">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#FFFDF7]" data-testid="share-error">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#2D2D2D] mb-2">Certificate Not Available</h2>
          <p className="text-[#5A5A5A] mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full">
              Visit LoBe
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-16 px-4 sm:px-6 bg-[#FFFDF7]" data-testid="share-page">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <Heart className="w-8 h-8 text-[#E8A0BF] fill-[#E8A0BF]" />
              <span 
                className="text-2xl font-bold tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <span className="text-[#2D2D2D]">Lo</span>
                <span className="text-[#E8A0BF]">Be</span>
              </span>
            </Link>
            <h1 
              className="text-xl sm:text-2xl font-bold text-[#2D2D2D]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Fun Certificate Preview
            </h1>
          </div>

          {/* Certificate Display - VIEW ONLY - No Download, No Share */}
          <div className="certificate-preview rounded-2xl shadow-xl p-6 sm:p-10 relative overflow-hidden" data-testid="shared-certificate">
            {/* Decorative borders */}
            <div className="absolute left-3 sm:left-5 top-6 bottom-6 w-3 sm:w-4 space-y-3">
              <div className="h-10 sm:h-12 bg-[#B4D2E7] rounded-sm" />
              <div className="h-10 sm:h-12 bg-[#E8A0BF] rounded-sm" />
              <div className="h-10 sm:h-12 bg-[#B4D2E7] rounded-sm" />
              <div className="h-10 sm:h-12 bg-[#E8A0BF] rounded-sm" />
            </div>
            <div className="absolute right-3 sm:right-5 top-6 bottom-6 w-3 sm:w-4 space-y-3">
              <div className="h-10 sm:h-12 bg-[#B4D2E7] rounded-sm" />
              <div className="h-10 sm:h-12 bg-[#E8A0BF] rounded-sm" />
              <div className="h-10 sm:h-12 bg-[#B4D2E7] rounded-sm" />
              <div className="h-10 sm:h-12 bg-[#E8A0BF] rounded-sm" />
            </div>

            <div className="text-center relative z-10 px-4 sm:px-8">
              <h2 
                className="text-2xl sm:text-3xl font-bold text-[#2D2D2D] mb-1" 
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                CERTIFICATE
              </h2>
              <p className="text-sm sm:text-base text-[#5A5A5A] mb-6">of Fun</p>
              
              <p className="text-xs sm:text-sm text-[#888888] mb-2">This novelty certificate is presented to:</p>
              
              <h3 
                className="font-script text-2xl sm:text-4xl text-[#C25E5E] mb-3" 
                data-testid="shared-names"
              >
                {certificate.your_name} & {certificate.partner_name}
              </h3>
              
              <div className="w-32 sm:w-40 h-px bg-[#E8A0BF] mx-auto mb-4" style={{ borderBottom: "2px dashed #E8A0BF" }} />
              
              <p className="text-[#5A5A5A] italic text-sm sm:text-base max-w-lg mx-auto leading-relaxed mb-6">
                "{certificate.description}"
              </p>
              
              {/* Meta Info - MASKED Certificate ID */}
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-[#888888] mb-6">
                <div className="flex items-center justify-center gap-2">
                  <span>{formatDate(certificate.created_at)}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Hash className="w-3 h-3 sm:w-4 sm:h-4" />
                  {/* MASKED ID for shared view */}
                  <span className="font-mono">{certificate.certificate_id_masked}</span>
                </div>
              </div>

              {certificate.is_ended && certificate.ended_at && (
                <p className="text-red-500 text-sm mb-4">
                  Ended: {formatDate(certificate.ended_at)}
                </p>
              )}
              
              {/* LoBe Seal - Bottom Right, Pink, Smaller */}
              <div className="flex justify-end pr-4">
                <div className="inline-flex flex-col items-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-[#E8A0BF] flex items-center justify-center">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[#E8A0BF] flex items-center justify-center">
                      <span className="text-[#E8A0BF] font-bold text-xs">LoBe</span>
                    </div>
                  </div>
                  <span className="text-[8px] sm:text-[10px] text-[#E8A0BF] mt-1 tracking-wider">CERTIFIED</span>
                </div>
              </div>
              
              {/* Hearts Footer */}
              <div className="mt-6 flex justify-center gap-3 text-xl text-[#E8A0BF]">
                <span>♥</span>
                <span>♥</span>
                <span>♥</span>
              </div>
            </div>
          </div>

          {/* No Download or Share buttons - View Only */}

          {/* Disclaimer */}
          <div className="mt-6 text-center text-xs text-[#888888] bg-[#F4F1EA] rounded-xl p-4">
            <p>
              This is a <strong>view-only preview</strong>. This certificate is for entertainment and novelty purposes only. 
              It has no legal validity and is not an official document.
            </p>
          </div>

          {/* CTA to create own */}
          <div className="mt-6 text-center">
            <Link to="/create">
              <Button className="bg-[#E8A0BF] hover:bg-[#D68C85] text-white rounded-full px-6 py-3 text-sm">
                <Heart className="w-4 h-4 mr-2" />
                Create Your Own Certificate
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SharePage;
